import { IInstallment } from "api/interfaces";
import { makeAutoObservable } from "mobx";
import AdvanceController from "../api/controllers/AdvanceController";
import AdvanceModel from "../api/models/AdvanceModel";
import AdvanceService from "../api/services/AdvanceService";
import InstallmentService from "../api/services/InstallmentService";
import { lang } from "../i18nLang";
import { ContractStore, MessageStore } from "./";

import { GreaterThanOrEqualException } from "../api/exceptions/InvalidValueException";
import { decimal } from "../utils/DecimalPrecision";

class Store {
  private observer: any = undefined;
  private _advances: AdvanceModel[] = [];
  private _currentAdvance: AdvanceModel | undefined = undefined;
  public readonly validations = {
    title: "required",
    date: "date",
    total: { required: true, greaterThan: { valueToCompare: 0 } },
    quantity: { required: true, between: { min: 1, max: 50 } },
  };

  constructor() {
    makeAutoObservable(this);
  }

  get currentAdvance() {
    return this._currentAdvance;
  }

  get advances() {
    return [...this._advances];
  }

  get advancesTotal() {
    return decimal(0)
      .sum(...this._advances.map((advance) => advance.total))
      .toNumber();
  }

  get advancesTotalToBill() {
    return decimal(0)
      .sum(
        ...this._advances.map((advance) =>
          decimal(advance.total).subtract(advance.totalLinked).toNumber()
        )
      )
      .toNumber();
  }

  private reset() {
    this.observer = undefined;
    this._advances = [];
    this._currentAdvance = undefined;
  }

  addAdvancesListObserver() {
    this.removeObserver();
    this.observer = AdvanceService.subscribeAdvancesSnapshot(
      ContractStore.getCurrentContract()?.uid || "",
      (advancesAdded) => this.addAdvancesToList(advancesAdded),
      (advancesChanged) => this.changeAdvancesOnList(advancesChanged),
      (advancesRemoved) => this.removeAdvancesFromList(advancesRemoved)
    );
  }

  private addAdvancesToList(advancesAdded: AdvanceModel[]) {
    this._advances = advancesAdded
      .concat(this._advances)
      .sort((a, b) => b.code - a.code);
  }

  private changeAdvancesOnList(advancesChanged: AdvanceModel[]) {
    const advances = this._advances;
    advancesChanged.forEach((adv) => {
      const index = advances.findIndex((a) => a.uid === adv.uid);
      if (index !== -1) advances[index] = adv;
    });
    this._advances = advances.sort((a, b) => b.code - a.code);
  }

  private removeAdvancesFromList(advancesRemoved: AdvanceModel[]) {
    const uids = advancesRemoved.map((adv) => adv.uid);
    this._advances = this._advances.filter((adv) => !uids.includes(adv.uid));
  }

  removeObserver() {
    if (this.observer) {
      this.observer();
    }
    this.reset();
  }

  clearCurrentAdvance() {
    this._currentAdvance = undefined;
  }

  setCurrentAdvance(uid: string) {
    this._currentAdvance = this._advances.find(
      (advance) => advance.uid === uid
    );
  }

  createAdvance(data) {
    AdvanceController.createAdvance(
      ContractStore.getCurrentContract()?.uid || "",
      {
        ...data,
        code: this.getNextCode(),
      }
    )
      .then(() => {
        MessageStore.success(lang("advances.success.created"));
      })
      .catch((error) => {
        console.error(error);
        MessageStore.error(error.messages?.long || error.message);
      });
  }

  private getNextCode(): number {
    let maxCode = 0;
    this._advances.forEach((advance) => {
      if (advance.code > maxCode) maxCode = advance.code;
    });
    return maxCode + 1;
  }

  public deleteAdvance() {
    if (!this._currentAdvance) return;
    AdvanceService.deleteAdvance(this._currentAdvance)
      .then(() => {
        this.removeAdvancesFromList([this._currentAdvance!]);
        MessageStore.success(lang("advances.success.deleted"));
      })
      .catch((error) => {
        console.error(error);
        MessageStore.error(error.message);
      });
  }

  updateAdvance(updatedData) {
    if (!this._currentAdvance) return;
    AdvanceController.updateAdvance(this._currentAdvance, updatedData)
      .then(() => {
        MessageStore.success(lang("advances.success.updated"));
      })
      .catch((error) => {
        console.error(error);
        if (error instanceof GreaterThanOrEqualException) {
          if (error.field === "total") {
            MessageStore.error(lang("advances.error.onUpdateTotal"));
            return;
          }
          if (error.field === "quantity") {
            MessageStore.error(lang("advances.error.onUpdateQuantity"));
            return;
          }
        }
        MessageStore.error(error.messages?.long || error.message);
      });
  }

  updateInstallmentMaturityDate(installmentId, newDate) {
    const advance = this._currentAdvance;
    if (!advance) return;

    advance.setInstallmentMaturityDate(installmentId, newDate);
    this.updateAdvanceInstallments(advance);
  }

  private updateAdvanceInstallments(advance: AdvanceModel) {
    if (!advance) return;

    AdvanceService.updateInstallments(advance).catch((error) => {
      console.error(error);
      MessageStore.error(error.message.long || error.message);
    });
  }

  createInstallments(
    { total, date, quantity },
    recalculate: boolean = false
  ): IInstallment[] {
    if (!recalculate)
      return InstallmentService.generateInstallments(date, quantity, total);

    if (this._currentAdvance)
      return InstallmentService.recalculateInstallments(
        this._currentAdvance,
        quantity,
        total,
        date
      );

    return [];
  }

  findAdvance(id: string): AdvanceModel | undefined {
    return this._advances.find((advance) => advance.uid === id);
  }
}

const AdvanceStore = new Store();
export default AdvanceStore;
